import React from 'react';
import {
    ArrowRight,
    MessageSquare,
    FileText,
    History,
    Loader2,
} from 'lucide-react';
import type { CustomerActivity } from '../../types';

export interface WorkflowHistoryTabProps {
    historyData: CustomerActivity[] | undefined;
    historyLoading: boolean;
}

const getActivityIcon = (type: string) => {
    switch (type) {
        case 'state_changed': return <ArrowRight className="w-4 h-4" />;
        case 'substatus_changed': return <MessageSquare className="w-4 h-4" />;
        case 'document_uploaded': return <FileText className="w-4 h-4" />;
        default: return <History className="w-4 h-4" />;
    }
};

const getActivityColor = (type: string) => {
    switch (type) {
        case 'state_changed': return 'bg-blue-100 text-blue-600';
        case 'substatus_changed': return 'bg-purple-100 text-purple-600';
        case 'document_uploaded': return 'bg-green-100 text-green-600';
        default: return 'bg-gray-100 text-gray-600';
    }
};

const WorkflowHistoryTab: React.FC<WorkflowHistoryTabProps> = ({ historyData, historyLoading }) => {
    return (
        <div className="bg-white rounded-lg shadow">
            {historyLoading ? (
                <div className="flex items-center justify-center p-12">
                    <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
                </div>
            ) : historyData && historyData.length > 0 ? (
                <div className="flow-root p-6">
                    <ul className="-mb-8">
                        {historyData.map((activity: CustomerActivity, activityIdx: number) => (
                            <li key={activity.id}>
                                <div className="relative pb-8">
                                    {activityIdx !== historyData.length - 1 ? (
                                        <span
                                            className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200"
                                            aria-hidden="true"
                                        />
                                    ) : null}
                                    <div className="relative flex space-x-3">
                                        <div>
                                            <span className={`h-8 w-8 rounded-full flex items-center justify-center ring-8 ring-white ${getActivityColor(activity.activity_type)}`}>
                                                {getActivityIcon(activity.activity_type)}
                                            </span>
                                        </div>
                                        <div className="flex min-w-0 flex-1 justify-between space-x-4 pt-1.5">
                                            <div>
                                                <p className="text-sm text-gray-500">
                                                    {activity.description || `${activity.field_name}: ${activity.old_value} → ${activity.new_value}`}
                                                </p>
                                                {activity.user_name && (
                                                    <p className="mt-1 text-xs text-gray-500">
                                                        Par {activity.user_name}
                                                    </p>
                                                )}
                                            </div>
                                            <div className="whitespace-nowrap text-right text-sm text-gray-500">
                                                <time dateTime={activity.created_at}>
                                                    {new Date(activity.created_at).toLocaleDateString('fr-FR')}
                                                </time>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            ) : (
                <div className="text-center py-12 text-gray-500">
                    Aucun historique disponible
                </div>
            )}
        </div>
    );
};

export default WorkflowHistoryTab;
